// 旋轉機關的「轉的方向」：順時針 / 逆時針（從上面看），和一排旋轉機關「左半 / 右半」反方向轉。
// 純函式，不靠 React，有測試（spin.test.ts）。
//
// 為什麼要這個：180° 和 −180° 停下來的樣子一樣，差在「從上一個位置怎麼轉過去」。導演不用自己打負數，按「順時針 / 逆時針」就好：
//   最後停的角度不變（只差 360 的倍數），只改轉的方向。
// 方向規則（跟 readouts.ts 的方向讀數同一套，由 spinDirOf 統一算）：
//   轉軸朝上（axisY > 0）：值變大 = 從上面看逆時針；轉軸朝下：值變大 = 順時針。

import type { Halves } from './scope';

export type SpinDir = 'cw' | 'ccw';

/** 少於這麼多度算「沒有轉動」 */
export const NO_TURN_DEG = 0.5;
const EPS = 1e-6;

/** 轉軸大致垂直才講得出「從上面看」的方向 */
export function isVerticalAxis(axisY: number): boolean {
  return Math.abs(axisY) >= 0.7;
}

export const oppositeDir = (d: SpinDir): SpinDir => (d === 'cw' ? 'ccw' : 'cw');
export const dirLabel = (d: SpinDir): string => (d === 'cw' ? '順時針' : '逆時針');

/** 值從上一個位置變成現在的值（差 delta 度）：從上面看是哪個方向；沒有轉動 / 轉軸不是垂直 → null */
export function spinDirOf(delta: number, axisY: number): SpinDir | null {
  if (!isVerticalAxis(axisY) || !(Math.abs(delta) >= NO_TURN_DEG)) return null;
  return delta * Math.sign(axisY) > 0 ? 'ccw' : 'cw';
}

export interface SpinInput {
  /** 上一個位置（歌頁 = 這首歌上一個 cue；Cue 庫頁 = 選的來源 cue；都沒有 = 預設位置） */
  prev: number;
  /** 現在的值（含還沒存的改動） */
  cur: number;
  travelMin: number;
  travelMax: number;
  /** 轉軸（場景座標）的 y */
  axisY: number;
}

export type SpinFail = 'axis' | 'noTurn' | 'range';

export type SpinResult =
  /** value = 要設成的值；changed = 跟現在的值不一樣；已經是這個方向 → value = cur、changed = false */
  | { ok: true; value: number; changed: boolean }
  | { ok: false; reason: SpinFail };

/**
 * 往 dir 方向轉的值：最後角度跟現在一樣（差 360 的倍數），從上一個位置往 dir 轉過去，而且在行程內。
 * 現在已經是這個方向 → 原樣不動（多轉了幾圈也保留）。不是 → 取離上一個位置最近的、方向對的那個。
 * 例：上一個 0、現在 180、轉軸朝上、要順時針 → −180。
 */
export function spinValueFor(i: SpinInput, dir: SpinDir): SpinResult {
  if (!isVerticalAxis(i.axisY)) return { ok: false, reason: 'axis' };
  const here = spinDirOf(i.cur - i.prev, i.axisY);
  if (here === null) return { ok: false, reason: 'noTurn' };
  if (here === dir) return { ok: true, value: i.cur, changed: false };
  const kMin = Math.ceil((i.travelMin - i.cur) / 360 - EPS);
  const kMax = Math.floor((i.travelMax - i.cur) / 360 + EPS);
  let best: number | null = null;
  for (let k = kMin; k <= kMax; k++) {
    const v = i.cur + 360 * k;
    const d = v - i.prev;
    if (spinDirOf(d, i.axisY) !== dir) continue;
    if (best === null || Math.abs(d) < Math.abs(best - i.prev) - EPS) best = v;
  }
  if (best === null) return { ok: false, reason: 'range' };
  return { ok: true, value: Math.round(best * 1e6) / 1e6, changed: true };
}

export interface SpinChoice {
  dir: SpinDir;
  /** 按得下去（會改值） */
  enabled: boolean;
  /** 現在就是這個方向 */
  active: boolean;
  /** 按下去要設成的值（不能按 → null） */
  value: number | null;
  /** 不能按的白話原因（能按 → null） */
  reason: string | null;
}

/** 不能按的原因（白話；range 的說明要行程） */
export function spinReasonText(reason: SpinFail, dir: SpinDir, travel: { travelMin: number; travelMax: number }): string {
  if (reason === 'noTurn') return '跟上一個位置一樣，沒有轉動，所以沒有方向可選。先把角度調成要轉的角度。';
  if (reason === 'range') {
    return `行程是 ${trim(travel.travelMin)}° 到 ${trim(travel.travelMax)}°，要停在這個角度又往${dirLabel(dir)}轉，會超出行程。`;
  }
  return '這個機關的轉軸不是垂直的，沒有順時針 / 逆時針可選。';
}
const trim = (n: number): string => String(Math.round(n * 10) / 10);

export function spinChoice(i: SpinInput, dir: SpinDir): SpinChoice {
  const r = spinValueFor(i, dir);
  if (!r.ok) return { dir, enabled: false, active: false, value: null, reason: spinReasonText(r.reason, dir, i) };
  return { dir, enabled: r.changed, active: !r.changed, value: r.value, reason: null };
}

export function spinChoices(i: SpinInput): { cw: SpinChoice; ccw: SpinChoice } {
  return { cw: spinChoice(i, 'cw'), ccw: spinChoice(i, 'ccw') };
}

// ───────── 一排：左半 / 右半反方向 ─────────

export interface SpinMember extends SpinInput { id: string }

export interface MirrorPlan {
  /** 要改的值（只有真的會變的） */
  values: Record<string, number>;
  /** 已經是要的方向的片數 */
  alreadyIds: string[];
  /** 沒辦法轉的片（沒有轉動 / 行程不夠）：保持原樣 */
  skipped: { id: string; reason: SpinFail }[];
  /** 正中間的片（不動） */
  centerIds: string[];
  /** 按得下去：至少有一片會變 */
  enabled: boolean;
  /** 整排都已經是這樣了 */
  active: boolean;
}

/**
 * 左半轉 leftDir、右半轉相反的方向；每片保持自己的最後角度，只改轉的方向。
 * halves 是觀眾看過去的左右（scope.ts 的 splitHalves）；不在左右半的（正中間）不動。
 */
export function mirrorPlan(members: SpinMember[], halves: Halves, leftDir: SpinDir): MirrorPlan {
  const left = new Set(halves.left), right = new Set(halves.right);
  const plan: MirrorPlan = { values: {}, alreadyIds: [], skipped: [], centerIds: [], enabled: false, active: false };
  for (const m of members) {
    const dir = left.has(m.id) ? leftDir : right.has(m.id) ? oppositeDir(leftDir) : null;
    if (dir === null) { plan.centerIds.push(m.id); continue; }
    const r = spinValueFor(m, dir);
    if (!r.ok) plan.skipped.push({ id: m.id, reason: r.reason });
    else if (r.changed) plan.values[m.id] = r.value;
    else plan.alreadyIds.push(m.id);
  }
  plan.enabled = Object.keys(plan.values).length > 0;
  plan.active = !plan.enabled && plan.alreadyIds.length > 0 && plan.skipped.length === 0;
  return plan;
}

/** 整排對稱按鈕不能按 / 有幾片沒動的白話說明 */
export function mirrorNote(plan: MirrorPlan, travel?: { travelMin: number; travelMax: number }): string | null {
  if (plan.skipped.length === 0) return null;
  const n = plan.skipped.length;
  const first = plan.skipped[0].reason;
  const why = first === 'noTurn' ? '沒有轉動' : first === 'range' ? `停在這個角度會超出行程${travel ? `（${trim(travel.travelMin)}° 到 ${trim(travel.travelMax)}°）` : ''}` : '轉軸不是垂直的';
  return `有 ${n} 片${why}，保持原樣。`;
}

// ───────── 一排旋轉機關有哪些 ─────────

interface RowDef {
  id: string; name: string; kind: string; axis: { y: number };
  groupId?: string | null; groupIndex?: number | null;
}

/** 名字裡的數字都換成 #，名字只差編號的就是同一排（「旋轉屏-1 旋轉」「旋轉屏-6 旋轉」） */
export function nameFamily(name: string): string | null {
  return /\d/.test(name) ? name.replace(/\d+/g, '#').trim() : null;
}

/**
 * 跟這個旋轉機關同一排的旋轉機關（含自己）：有 groupId 就是同 groupId 的；沒有（或同排只有自己）就找名字只差編號的。
 * 只算垂直轉軸的旋轉機關；找不到另一個 → 只有自己（沒有對稱可選）。
 */
export function spinRowMembers<T extends RowDef>(defs: T[], def: T): T[] {
  const spin = (d: T) => d.kind === 'rotary' && isVerticalAxis(d.axis.y);
  if (!spin(def)) return [def];
  if (def.groupId) {
    const g = defs
      .map((d, i) => ({ d, i }))
      .filter(x => x.d.groupId === def.groupId && spin(x.d))
      .sort((a, b) => ((a.d.groupIndex ?? 1e9) - (b.d.groupIndex ?? 1e9)) || (a.i - b.i))
      .map(x => x.d);
    if (g.length > 1) return g;
  }
  const fam = nameFamily(def.name);
  if (fam === null) return [def];
  const sib = defs.filter(d => spin(d) && nameFamily(d.name) === fam);
  return sib.length > 1 ? sib : [def];
}

/** 選了好幾個：全部都在同一排旋轉機關（至少兩片）→ 回其中一片，整排左右對稱用它算；混到別的機關 → null */
export function commonSpinRowMember<T extends RowDef>(defs: T[], picked: T[]): T | null {
  if (picked.length < 2) return null;
  const row = spinRowMembers(defs, picked[0]);
  if (row.length < 2) return null;
  return picked.every(p => row.some(r => r === p)) ? picked[0] : null;
}
